// Lib
import React from 'react';
import { Capacitor } from '@capacitor/core';

// Utils
import { getSubscription } from '../../../../../../common/users/utils/userPropertyUtils';
import {
    getSubscriptionPlanMaxSeats,
    getSubscriptionPlanName,
    getSubscriptionPlanProductPricingScheme,
} from '../../../../../../common/payment/subscriptionPropertyUtil';

import { isUserSubscriptionAdmin } from '../../../../../user/userUtil';
import { isAppStoreSubscription } from '../../../../../../common/payment/subscriptions/subscriptionUtils';
import { InAppPurchases } from '../../../../../../capacitor_plugins/inAppPurchases';

// Components
import ElectronLink from '../../../../../components/electron/ElectronLink';
import PopupPanelAsideSection from '../../../../../components/popupPanel/types/asideSection/PopupPanelAsideSection';
import PopupActionsSection from '../../../../../components/popupPanel/types/actionsSection/PopupActionsSection';
import LinkButton from '../../../../../components/buttons/LinkButton';
import ApplicationTypeSwitch from '../../../../../platform/components/ApplicationTypeSwitch';

// Constants
import { CHECKOUT_TEAM_MEMBERS_ROUTE } from '../../../../../user/account/checkout/checkoutConstants';
import { CAPACITOR_IN_APP_PURCHASES_PLUGIN_NAME } from '../../../../../../capacitor_plugins/pluginConstants';

// Types
import { ProductPricingScheme } from '../../../../../../common/payment/products/productTypes';
import { MNSubscription } from '../../../../../../common/payment/subscriptions/subscriptionTypes';
import { ApplicationType } from '../../../../../../common/platform/platformTypes';
import { ImMNCurrentUser } from '../../../../../user/currentUserSelector';

const DesktopSubscriptionManagement = ({
    showManageTeamButton,
    manageTeamURL,
}: {
    showManageTeamButton: boolean;
    manageTeamURL: string;
}) => (
    <PopupActionsSection>
        <ElectronLink to="/account/plan">View billing details</ElectronLink>
        {showManageTeamButton && <ElectronLink to={manageTeamURL}>Manage team</ElectronLink>}
    </PopupActionsSection>
);

const IOSSubscriptionManagement = ({ subscription }: { subscription: MNSubscription }) => {
    if (!isAppStoreSubscription(subscription)) {
        return <div className="upgrade-button-visit-milanote">Visit milanote.com to manage billing details</div>;
    }

    const onCapacitorManageButtonClick = async () => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_IN_APP_PURCHASES_PLUGIN_NAME)) return;
        await InAppPurchases.presentAppleManagePurchases();
    };

    return (
        <div className="manage-subscription-button">
            <LinkButton onClick={onCapacitorManageButtonClick}>Manage your plan</LinkButton>
        </div>
    );
};

type BillingTierProProps = {
    currentUser: ImMNCurrentUser;
};

const BillingTierPro = ({ currentUser }: BillingTierProProps) => {
    const subscription = getSubscription(currentUser);
    const planName = getSubscriptionPlanName(subscription) || 'professional';
    const currentUserIsAdmin = isUserSubscriptionAdmin(currentUser, subscription);

    const hasMultipleMembers =
        getSubscriptionPlanProductPricingScheme(subscription) === ProductPricingScheme.TIERED ||
        getSubscriptionPlanMaxSeats(subscription) > 1;

    const showManageTeamButton = currentUserIsAdmin && !isAppStoreSubscription(subscription) && hasMultipleMembers;

    const SubscriptionManagementIOSCapacitor = () => <IOSSubscriptionManagement subscription={subscription} />;
    const SubscriptionManagementUnavailable = () => null;
    const SubscriptionManagementDesktop = () => (
        <DesktopSubscriptionManagement
            showManageTeamButton={showManageTeamButton}
            manageTeamURL={CHECKOUT_TEAM_MEMBERS_ROUTE}
        />
    );

    return (
        <PopupPanelAsideSection className="billing-status">
            <h3>You’re on the {planName} plan</h3>

            <div className="plan-storage">Unlimited storage</div>

            <ApplicationTypeSwitch
                components={{
                    [ApplicationType.iphoneCapacitor]: SubscriptionManagementIOSCapacitor,
                    [ApplicationType.ipadCapacitor]: SubscriptionManagementIOSCapacitor,
                    [ApplicationType.androidCapacitor]: SubscriptionManagementUnavailable,
                    [ApplicationType.androidTabletCapacitor]: SubscriptionManagementUnavailable,
                    [ApplicationType.androidPhonePwa]: SubscriptionManagementUnavailable,
                    [ApplicationType.androidTabletPwa]: SubscriptionManagementUnavailable,
                    [ApplicationType.iframe]: SubscriptionManagementUnavailable,
                    default: SubscriptionManagementDesktop,
                }}
            />
        </PopupPanelAsideSection>
    );
};

export default BillingTierPro;
